import type { ReactNode } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const ghostTileClassName =
  "group flex h-full w-full flex-col items-center justify-center gap-2.5 rounded-xl border border-dashed px-3 py-5 text-center";

export const GhostTile = ({
  label,
  icon,
  href,
  onClick,
  action,
  className,
  ariaLabel
}: {
  label: string;
  icon: ReactNode;
  href?: string;
  onClick?: () => void;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
  ariaLabel?: string;
}) => {
  const content = (
    <>
      {icon}
      <span className="font-medium text-muted-foreground group-hover:text-foreground">
        {label}
      </span>
      {action ? (
        <Button
          type="button"
          size="sm"
          className="mt-1"
          onClick={event => {
            event.stopPropagation();
            action.onClick();
          }}
        >
          {action.label}
        </Button>
      ) : null}
    </>
  );

  const mergedClassName = cn(ghostTileClassName, className);

  if (action) {
    return (
      <div aria-label={ariaLabel ?? label} className={mergedClassName}>
        {content}
      </div>
    );
  }

  if (href) {
    const external = href.startsWith("http");

    return (
      <Link
        href={href}
        aria-label={ariaLabel ?? label}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer noopener" : undefined}
        className={mergedClassName}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      aria-label={ariaLabel ?? label}
      onClick={onClick}
      className={mergedClassName}
    >
      {content}
    </button>
  );
};
